import type { AuthScheme } from "../components/AuthConfigStep";
import type { OverrideMode } from "../components/AuthOverrideStep";
import { needsBaseUrlOverride } from "./baseUrl";
import type { PreviewApiResponse } from "./previewResponse";

export type AuthSetupUiFlags = {
  showSetupSection: boolean;
  showBaseUrlStep: boolean;
  showAuthOverride: boolean;
  showAuthConfig: boolean;
  showGenerateCard: boolean;
};

export type AuthSetupDebugSnapshot = {
  ts: string;
  source: string;
  parsedRawData: boolean;
  lastConvertSuccess: boolean;
  baseUrl: string;
  showBaseUrlInput: boolean;
  baseUrlReady: boolean;
  authSchemesRaw: unknown;
  authSchemes: AuthScheme[];
  parsedDataAuth: unknown;
  usableSchemesFromSpec: AuthScheme[];
  onlyComplexAuth: boolean;
  needsManualOverride: boolean;
  overrideReason: string;
  authOverride: OverrideMode | null;
  authSchemesForSetup: AuthScheme[];
  detectAuthType: string;
  authReady: boolean;
  ui: AuthSetupUiFlags;
  hideInlineSetup: boolean;
  setupSlotPassed: boolean;
};

const PREFIX = "[AuthSetup]";

export function buildAuthSetupUiFlags(params: {
  showSetupSection: boolean;
  showBaseUrlInput: boolean;
  needsManualOverride: boolean;
  authSchemesForSetup: AuthScheme[];
  detectAuthTypeResult: string;
}): AuthSetupUiFlags {
  const showAuthConfig =
    params.authSchemesForSetup.length > 0 && params.detectAuthTypeResult !== "none";
  return {
    showSetupSection: params.showSetupSection,
    showBaseUrlStep: params.showSetupSection && params.showBaseUrlInput,
    showAuthOverride: params.showSetupSection && params.needsManualOverride,
    showAuthConfig: params.showSetupSection && showAuthConfig,
    showGenerateCard: params.showSetupSection,
  };
}

/** Log immediately after /api/v1/preview returns (before React state updates). */
export function logPreviewAuthResponse(source: string, data: PreviewApiResponse): void {
  const raw = data.spec?.auth_schemes ?? null;
  const schemes = Array.isArray(raw)
    ? raw.map(s => ({ type: s.type || "", scheme: s.scheme, name: s.name }))
    : [];
  const onlyOAuth =
    schemes.length > 0 && schemes.every(s => s.type === "oauth2" || s.type === "openIdConnect");
  const usable = schemes.filter(s => s.type === "http" || s.type === "apiKey");

  logAuthSetupSnapshot(source, {
    ts: new Date().toISOString(),
    source,
    parsedRawData: true,
    lastConvertSuccess: false,
    baseUrl: data.spec?.base_url ?? "",
    showBaseUrlInput: needsBaseUrlOverride(data.spec?.base_url),
    baseUrlReady: false,
    authSchemesRaw: raw,
    authSchemes: schemes,
    parsedDataAuth: schemes[0] ?? null,
    usableSchemesFromSpec: usable,
    onlyComplexAuth: onlyOAuth,
    needsManualOverride: usable.length === 0,
    overrideReason: onlyOAuth ? "oauth2" : "missing",
    authOverride: null,
    authSchemesForSetup: usable,
    detectAuthType: usable.length ? (usable[0].scheme || usable[0].type) : "none",
    authReady: false,
    ui: {
      showSetupSection: true,
      showBaseUrlStep: needsBaseUrlOverride(data.spec?.base_url),
      showAuthOverride: usable.length === 0,
      showAuthConfig: usable.length > 0,
      showGenerateCard: true,
    },
    hideInlineSetup: true,
    setupSlotPassed: true,
  });
}

export function isAuthSetupDebugEnabled(): boolean {
  if (typeof window === "undefined") return true;
  try {
    if (import.meta.env.DEV) return true;
    if (localStorage.getItem("auth-setup-debug") === "1") return true;
    return new URLSearchParams(window.location.search).has("auth_debug");
  } catch {
    return true;
  }
}

export function logAuthSetupSnapshot(source: string, snap: AuthSetupDebugSnapshot): void {
  const row = {
    source: snap.source,
    setupSection: snap.ui.showSetupSection,
    baseUrl: snap.ui.showBaseUrlStep,
    override: snap.ui.showAuthOverride,
    config: snap.ui.showAuthConfig,
    generate: snap.ui.showGenerateCard,
    needsManualOverride: snap.needsManualOverride,
    authReady: snap.authReady,
    lastConvertSuccess: snap.lastConvertSuccess,
    detectAuthType: snap.detectAuthType,
    schemeCount: snap.authSchemes.length,
    usableCount: snap.usableSchemesFromSpec.length,
  };

  // Always emit one line in production so an empty console is not mistaken for "no code ran".
  console.warn(`${PREFIX} ${source}`, row);

  if (!isAuthSetupDebugEnabled()) return;

  console.groupCollapsed(`${PREFIX} ${source} · ${snap.ts}`);
  console.table(row);
  console.log(`${PREFIX} raw auth_schemes from API:`, snap.authSchemesRaw);
  console.log(`${PREFIX} normalized authSchemes:`, snap.authSchemes);
  console.log(`${PREFIX} usableSchemesFromSpec:`, snap.usableSchemesFromSpec);
  console.log(`${PREFIX} authSchemesForSetup:`, snap.authSchemesForSetup);
  console.log(`${PREFIX} preview auth summary:`, snap.parsedDataAuth);
  console.log(`${PREFIX} full snapshot:`, snap);
  if (!snap.ui.showSetupSection) {
    console.warn(
      `${PREFIX} Setup UI hidden — parsedRawData=${snap.parsedRawData}, lastConvertSuccess=${snap.lastConvertSuccess}`,
    );
  }
  if (snap.ui.showSetupSection && !snap.ui.showAuthOverride && !snap.ui.showAuthConfig && !snap.ui.showBaseUrlStep) {
    console.warn(`${PREFIX} Setup section mounted but no auth/base-url child rendered`);
  }
  console.groupEnd();
}
